import type { DateFormat } from "@/components/ui/dateFormatUtils";

export type RegionCode = "AU" | "US";

export interface RegionConfig {
    code: RegionCode;
    locale: string;
    currencyCode: string;
    currencyPrefix: string;
    dateFormat: Intl.DateTimeFormatOptions;
    dateInputFormat: DateFormat;
    address: {
        hasSuburb: boolean;
        postalCodeLength: number;
        postalCodeLabel: string;
        stateLabel: string;
    };
    payFrequencyLabels: Record<string, string>;
    identifiers: {
        businessIdLabel: string;
        businessIdPlaceholder: string;
        businessIdLength: number;
        taxIdLabel: string;
    };
    sectionLabels: {
        superannuation: string;
        governmentPension: string;
        paygIncome: string;
    };
    businessStructureOptions: Array<{ value: string; label: string }>;
}

export const AU_CONFIG: RegionConfig = {
    code: "AU",
    locale: "en-AU",
    currencyCode: "AUD",
    currencyPrefix: "A$",
    dateFormat: { day: "numeric", month: "short", year: "numeric" },
    dateInputFormat: "DD/MM/YYYY",
    address: {
        hasSuburb: true,
        postalCodeLength: 4,
        postalCodeLabel: "Postcode",
        stateLabel: "State",
    },
    payFrequencyLabels: {
        weekly: "Weekly",
        fortnightly: "Fortnightly",
        monthly: "Monthly",
        quarterly: "Quarterly",
        annually: "Annually",
    },
    identifiers: {
        businessIdLabel: "ABN",
        businessIdPlaceholder: "Enter 11-digit ABN",
        businessIdLength: 11,
        taxIdLabel: "TFN",
    },
    sectionLabels: {
        superannuation: "Superannuation",
        governmentPension: "Government Pension",
        paygIncome: "PAYG Income",
    },
    businessStructureOptions: [
        { value: "Sole Trader", label: "Sole Trader" },
        { value: "Partnership", label: "Partnership" },
        { value: "Company", label: "Company" },
        { value: "Trust", label: "Trust" },
    ],
};

export const US_CONFIG: RegionConfig = {
    code: "US",
    locale: "en-US",
    currencyCode: "USD",
    currencyPrefix: "$",
    dateFormat: { month: "short", day: "numeric", year: "numeric" },
    dateInputFormat: "MM/DD/YYYY",
    address: {
        hasSuburb: false,
        postalCodeLength: 5,
        postalCodeLabel: "ZIP Code",
        stateLabel: "State",
    },
    payFrequencyLabels: {
        weekly: "Weekly",
        fortnightly: "Bi-weekly",
        monthly: "Monthly",
        quarterly: "Quarterly",
        annually: "Annually",
    },
    identifiers: {
        businessIdLabel: "EIN",
        businessIdPlaceholder: "Enter 9-digit EIN",
        businessIdLength: 9,
        taxIdLabel: "SSN",
    },
    sectionLabels: {
        superannuation: "401k / Retirement Income",
        governmentPension: "Social Security",
        paygIncome: "Salaried Income",
    },
    businessStructureOptions: [
        { value: "Sole Trader", label: "Sole Proprietorship" },
        { value: "Partnership", label: "Partnership" },
        { value: "Company", label: "Corporation" },
        { value: "Trust", label: "Trust" },
    ],
};

export const REGION_CONFIGS: Record<RegionCode, RegionConfig> = {
    AU: AU_CONFIG,
    US: US_CONFIG,
};

export const DEFAULT_REGION: RegionCode = "AU";
